// @flow

import React, {Component, Fragment} from 'react';
import gql from 'graphql-tag';
import {FormattedMessage} from 'react-intl';
import PriceSummary from '@homeaway/react-price-summary';
import {currencyFormatter} from '@homeaway/hacommons-i18n';
import InstantBookIcon from '../InstantBookIcon/InstantBookIcon';
import type {IntlShape} from 'react-intl';

import './Price.less';

export const fragments = {
    SERPHit: gql`
        fragment PriceSERPHit on SERPHit {
            price {
                currencyUnits
                periodType
                value
            }
        }
    `,
    listing: gql`
        fragment PriceListing on Listing {
            priceSummary {
                edapEventJson
                formattedAmount
                pricePeriodDescription
            }
        }
    `
};

export type PriceShape = {
    currencyUnits: string,
    periodType: string,
    value: number
};

export type SERPHitShape = {
    price: ?PriceShape
};

export type PriceSummaryShape = {
    edapEventJson: string,
    formattedAmount: string,
    pricePeriodDescription: string
};

export type ListingShape = {
    priceSummary: ?PriceSummaryShape
};

export type Props = {
    hit: SERPHitShape,
    listing: ListingShape,
    useAverage?: boolean,
    useShort?: boolean,
    fullLocale: string
};

type WithInstantBookProps = {
    intl: IntlShape,
    instantBookable?: boolean
} & Props;

const TOTAL_PERIOD_TYPE = 'TOTAL';

const PeriodFormatted = ({useAverage, useShort, useTotal}) => {
    let id;
    let defaultMessage;

    if (useShort) {
        id = 'ratesNightlyShort';
        defaultMessage = '/ night';
    } else {
        id = useAverage ? 'ratesAverage' : 'ratesNightly';
        defaultMessage = useAverage ? 'avg/night' : 'per night';
    }

    if (useTotal) {
        id = 'ratesTotal';
        defaultMessage = 'Total price';
    }

    return (
        <FormattedMessage
            id={id}
            description={'price period'}
            defaultMessage={defaultMessage}
        >
            {(txt) => txt}
        </FormattedMessage>
    );
};
export class Price extends Component<Props> {
    render() {
        if (this.props.listing.priceSummary) {
            return (
                <PriceSummary priceSummary={this.props.listing.priceSummary} />
            );
        }

        /* the following node-hacommons-i18n can directly produce the correct currency format and symbol given
        the locale and the currency (e.g., 'USD').  */
        const {
            hit: {price},
            useAverage,
            useShort,
            fullLocale
        } = this.props;
        const {currencyUnits, value, periodType} = price || {};

        if (currencyUnits && value) {
            const useTotal = periodType === TOTAL_PERIOD_TYPE;

            return (
                <Fragment>
                    <span
                        className="Price__value"
                        data-wdio="Price"
                        data-price={value}
                    >
                        {`${currencyFormatter.formatNumber({
                            value,
                            currencyCode: currencyUnits,
                            locale: fullLocale
                        })} `}
                    </span>
                    <PeriodFormatted
                        useAverage={useAverage}
                        useTotal={useTotal}
                        useShort={useShort}
                    />
                </Fragment>
            );
        }

        return (
            <span
                className="Price Price--noPrice"
                data-wdio="Price"
                data-price="0"
            >
                <FormattedMessage
                    id={'ratesNone'}
                    description={'price period'}
                    defaultMessage={'avg/night'}
                >
                    {(txt) => (
                        <Fragment>
                            {value} {txt}
                        </Fragment>
                    )}
                </FormattedMessage>
            </span>
        );
    }
}

export default function PriceWithInstantBook(props: WithInstantBookProps) {
    return (
        <Fragment>
            {props.instantBookable && <InstantBookIcon intl={props.intl} />}
            <Price {...props} />
        </Fragment>
    );
}
